import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdNewspaper, MdOpenInNew } from "react-icons/md";

type NewsItem = {
  title: string;
  link: string;
  pubDate: string;
  summary: string;
  source: string;
  source_key?: string;
  slug: string;
};

function formatDate(pubDate: string): string {
  // RFC 822 → Türkçe kısa tarih
  try {
    const d = new Date(pubDate);
    if (isNaN(d.getTime())) return pubDate;
    const aylar = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
    return `${d.getDate()} ${aylar[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return pubDate;
  }
}

export default function MevzuatHaberleriWidget({ limit = 6 }: { limit?: number }) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`https://api.maliajanda.com/api/mevzuat-haberleri?limit=${limit}`)
      .then(r => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(d => {
        setItems(Array.isArray(d) ? d : []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [limit]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-400">
        Mevzuat haberleri yükleniyor...
      </div>
    );
  }

  if (error || items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-500">
            <MdNewspaper className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-outfit text-base font-bold text-navy-700">Güncel Mevzuat Haberleri</h3>
            <p className="text-[11px] text-gray-500">Sektörden son güncellemeler</p>
          </div>
        </div>
        <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-600">
          ● Canlı
        </span>
      </div>

      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i}>
            {/* Başlık + doğrudan kaynak linki; özet metni yayınlanmıyor
                (bkz. MevzuatHaberleri.tsx'teki açıklama). */}
            <a
              href={item.link}
              target="_blank"
              rel="noopener nofollow"
              className="group block rounded-xl border border-gray-100 p-3 transition hover:border-brand-300 hover:bg-brand-50/30"
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <h4 className="line-clamp-2 text-sm font-semibold text-navy-700 group-hover:text-brand-600">
                      {item.title}
                    </h4>
                    <MdOpenInNew className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-brand-500" />
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[10px] text-gray-400">
                    <span>{formatDate(item.pubDate)}</span>
                    <span>·</span>
                    <span className="font-medium text-gray-500">{item.source}</span>
                  </div>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-2 text-[10px] text-gray-400">
        <span>Resmî Gazete · Alomaliye</span>
        <Link to="/mevzuat-haberleri" className="font-bold text-brand-500 hover:underline">
          Tümünü Gör →
        </Link>
      </div>
    </div>
  );
}
